import PropTypes from 'prop-types';
import React from 'react';

import gridStyles from 'assets/style/grid.module.css';
import clsx from 'clsx';

import { Hero } from 'js/components/Hero';
import InfoBlock from 'js/components/InfoColumnBlocks/InfoBlock';
import { Aliases } from 'js/components/Aliases';
import InfoColumnBlocks from 'js/components/InfoColumnBlocks';
import { InfoColumnLinks } from 'js/components/InfoColumnLinks/InfoColumnLinks';
import ContentSection from 'js/pages/TreatmentPage/ContentSection';
import { Markdown } from 'js/components/generic/Markdown';
import { VenueSearch } from 'js/components/VenueSearch';
import TopCities from 'js/components/TopCities';
import treatmentToBlock from 'js/components/InfoColumnBlocks/transform/treatment';
import treatmentTypeToBlock from 'js/components/InfoColumnBlocks/transform/treatment-type';
import inspirationArticleToBlock from 'js/components/InfoColumnBlocks/transform/inspiration-article';
import partial from 'lodash.partial';
import { createCityBlocksViewModelForBestForBrowse } from 'js/pages/TreatmentTypePage/create-city-blocks-view-model-for-best-for-browse.ts';
import { trackStructuredEvent } from 'js/helpers/google-tag-manager';
import { useTrackPageAndSessionView } from 'js/hooks/useTrackPageAndSessionView';
import styles from './treatmentTypePageStyles.module.css';
import { substituteWithDictionary } from '../../helpers/i18n';
import { CityBlocks } from '../../components/CityBlocks';
import { Context } from '../../components/LocaleWrapper';

const TRACKING_CATEGORY = 'treatment_guide';
const TRACKING_PROPERTY = 'btt_link';

const getHeroStyleProps = (cmsValues, getMediaUriCallback) => ({
  textColor: 'var(--color-grey-90)',
  bgColorFrom: cmsValues['background-color-from'],
  bgColorTo: cmsValues['background-color-to'],
  bgImageTablet: getMediaUriCallback(cmsValues['background-image-tablet']),
  bgImageDesktop: getMediaUriCallback(cmsValues['background-image-desktop']),
});

const generateTreatmentUri = (generateUri, block) =>
  generateUri('treatment', { normalisedName: block.normalisedName });

const TreatmentTypePage = () => {
  const {
    generateUri,
    i18n,
    getMediaUri,
    pageData: { treatmentType: pageData },
  } = React.useContext(Context);
  useTrackPageAndSessionView();
  const popularLocations = pageData.popularLocations;
  const treatmentType = pageData.treatmentType;
  const treatmentTypeId = treatmentType.id;
  const treatmentTypeNormalisedName = treatmentType.normalisedName;
  const treatmentTypeName = treatmentType.name;
  const otherTreatments = treatmentType.otherTreatmentCategories;
  const treatmentTypeCms = i18n(`page.treatment-type.${treatmentTypeId}`);
  const heroCms = treatmentTypeCms.hero;
  const heroStylesCms = heroCms.styles;
  const heroStyleProps = getHeroStyleProps(heroStylesCms, getMediaUri);
  const popularTreatments = treatmentToBlock(
    treatmentType.popularTreatmentCategories,
    generateUri,
    i18n
  );
  const otherTreatmentTypes = treatmentTypeToBlock(
    pageData.alternativeTreatmentTypes,
    generateUri,
    i18n
  );
  const otherTreatmentsTitle = i18n(
    'page.treatment-type.section-other-treatments',
    treatmentType.name.toLowerCase()
  );
  const inspirationArticles =
    inspirationArticleToBlock(pageData.treatmentTypesInspirationArticles) || [];

  const breadcrumbParams = {
    paths: [
      {
        name: i18n('page.treatment-guide.title'),
        pageType: 'treatmentGuide',
      },
      {
        name: treatmentType.name,
      },
    ],
    customColor: styles.breadcrumbs,
    customHoverColor: styles['breadcrumbs-link'],
  };

  const browseUriValues = {
    treatmentType: treatmentTypeNormalisedName,
    offerResultType: 'LOCAL',
  };
  const customTreatmentBlocks = (
    <VenueSearch
      key="searchVenue"
      title={i18n('venue-search.treatment-type-title').replace(
        '{entity-type}',
        treatmentTypeName
      )}
      typeAhead={{ treatmentIds: treatmentTypeId }}
      browseUriValues={browseUriValues}
      popularLocations={popularLocations}
    >
      <TopCities
        title={i18n('top-cities.title')}
        showMore={i18n('top-cities.show-more')}
        browseUriValues={browseUriValues}
      />
    </VenueSearch>
  );

  const cityBlocksViewModel = createCityBlocksViewModelForBestForBrowse(
    pageData.treatmentCategoryGroupBestForBrowsePages
  );

  const bestForCms = i18n(
    'page.treatment-type.best-for-browse-pages-title-template'
  );

  const bestForBrowsePagesHeading = substituteWithDictionary(bestForCms, {
    treatment: pageData.treatmentType.name,
  });

  const handleBestForBrowseExpandCity = (cityName) => {
    trackStructuredEvent({
      category: TRACKING_CATEGORY,
      property: TRACKING_PROPERTY,
      action: 'expand',
      value: pageData.treatmentCategoryGroupBestForBrowsePages.find(
        (bestForBrowsePage) => bestForBrowsePage.location === cityName
      ).treatments.length,
    });
  };

  const handleBestForBrowseTreatmentSelection = (cityName, treatmentName) => {
    const bestForBrowseCity =
      pageData.treatmentCategoryGroupBestForBrowsePages.find(
        (bestForBrowsePage) => bestForBrowsePage.location === cityName
      );

    trackStructuredEvent({
      category: TRACKING_CATEGORY,
      property: TRACKING_PROPERTY,
      action: 'click',
      label: bestForBrowseCity.treatments.find(
        (treatment) => treatment.name === treatmentName
      ).id,
      value: bestForBrowseCity.treatments.length,
    });
  };

  return (
    <div className={styles['treatment-type']}>
      <Hero
        title={heroCms.title}
        description={heroCms.description}
        breadcrumbParams={breadcrumbParams}
        {...heroStyleProps}
      >
        <div className={styles['aliases-wrapper']}>
          <Aliases aliases={treatmentType.aliases} />
        </div>
      </Hero>

      <div
        className={clsx(styles.wrapper, gridStyles.row, styles.firstSection)}
      >
        <InfoColumnBlocks
          blocks={popularTreatments}
          customBlock={customTreatmentBlocks}
          contentBlock={InfoBlock}
        />
      </div>
      {cityBlocksViewModel.cities.length !== 0 && (
        <ContentSection>
          <div className={styles['best-for-browse-container']}>
            <h2 className={styles['best-for-browse-header']}>
              {bestForBrowsePagesHeading}
            </h2>
            <CityBlocks
              {...cityBlocksViewModel}
              onCitySelect={handleBestForBrowseExpandCity}
              onTreatmentTypeSelect={handleBestForBrowseTreatmentSelection}
            />
          </div>
        </ContentSection>
      )}
      <div className={clsx(styles.wrapper, gridStyles.row)}>
        <InfoColumnLinks
          url={partial(generateTreatmentUri, generateUri)}
          title={otherTreatmentsTitle}
          links={otherTreatments}
        />
      </div>
      <div className={styles['section-content']}>
        <ContentSection>
          <div
            className={clsx(gridStyles['col-xs-12'], gridStyles['col-md-8'])}
          >
            <Markdown>{treatmentTypeCms['long-description']}</Markdown>
          </div>
        </ContentSection>
      </div>

      {inspirationArticles.length ? (
        <div className={clsx(styles.wrapper, gridStyles.row)}>
          <InfoColumnBlocks
            title={i18n('page.treatment-type.section-inspiration-articles')}
            blocks={inspirationArticles}
            contentBlock={InfoBlock}
          />
        </div>
      ) : null}

      <div className={clsx(styles.wrapper, gridStyles.row)}>
        <InfoColumnBlocks
          title={i18n('page.treatment-type.section-other-treatment-type')}
          blocks={otherTreatmentTypes}
          contentBlock={InfoBlock}
        />
      </div>
    </div>
  );
};

TreatmentTypePage.propTypes = {
  treatmentType: PropTypes.object,
  treatmentCategoryGroupBestForBrowsePages: PropTypes.shape({
    location: PropTypes.string,
    treatments: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        uri: PropTypes.string,
      })
    ),
  }),
};

export default TreatmentTypePage;
